import React, { useState, useEffect, useRef } from 'react';
import { Box } from '@mui/material';

interface DialogCustomProps {
  children: React.ReactNode;
  cellElement?: HTMLElement; // Cell element for positioning
  onFinished?: () => void; // finished in Vue
  minWidth?: number | string; // Optional minWidth override (default: 186px)
  padding?: number | string; // Optional padding override (default: 16px)
}

export const DialogCustom: React.FC<DialogCustomProps> = ({
  children,
  cellElement,
  onFinished,
  minWidth,
  padding,
}) => {
  // Parse initial value
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const dialogCustomRef = useRef<HTMLDivElement>(null);

  // Calculate position based on cell element and update when needed
  useEffect(() => {
    const updatePosition = () => {
      if (!cellElement || !dialogCustomRef.current) return;

      const cellRect = cellElement.getBoundingClientRect();
      const dialogCustomElement = dialogCustomRef.current;
      const dialogCustomHeight = dialogCustomElement.offsetHeight || 300;
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - cellRect.bottom;
      const spaceAbove = cellRect.top;

      // Determine if should position above
      const shouldPositionAbove =
        spaceBelow < dialogCustomHeight && spaceAbove > dialogCustomHeight;

      // Calculate position using fixed positioning (relative to viewport)
      const top = shouldPositionAbove ? cellRect.top - dialogCustomHeight - 4 : cellRect.bottom + 4;
      const left = cellRect.left;

      setPosition({ top, left });
    };

    // Initial position calculation - use window.requestAnimationFrame for immediate positioning
    const rafId = window.requestAnimationFrame(() => {
      updatePosition();
      // Second RAF to ensure accurate height measurement after render
      window.requestAnimationFrame(updatePosition);
    });

    // Update on scroll and resize
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [cellElement]);

  // Handle click outside to close dialog custom
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!dialogCustomRef.current) return;

      const target = event.target as Node;

      // Don't close if clicking on the dialog itself
      if (dialogCustomRef.current.contains(target)) {
        return;
      }

      // Don't close if clicking on the cell element (might be opening new editor)
      if (cellElement?.contains(target)) {
        return;
      }

      // Check if target is a table cell (might be opening new editor)
      // Delay closing to allow new editor to open first
      const targetElement = target as HTMLElement;
      if (targetElement.closest?.('td, th')) {
        // Small delay to allow new editor to open
        setTimeout(() => {
          // Double check if dialog still exists and cell is still valid
          if (dialogCustomRef.current && cellElement && document.contains(cellElement)) {
            onFinished?.();
          }
        }, 50);
        return;
      }

      // For other clicks outside, close immediately
      onFinished?.();
    };

    // Use capture phase to catch events before they bubble
    // But with a small delay to allow event propagation
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside, true);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [cellElement, onFinished]);

  const dialogCustomBox = (
    <Box
      ref={dialogCustomRef}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      sx={{
        backgroundColor: '#FFFFFF',
        borderRadius: '8px',
        padding: padding ?? '16px', // Allow override, default to 16px
        minWidth: minWidth ?? '186px', // Allow override, default to 186px for datepicker
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        position: 'fixed',
        zIndex: 99999,
        isolation: 'isolate', // Create new stacking context
        ...(position
          ? {
              top: `${position.top}px`,
              left: `${position.left}px`,
            }
          : {
              top: '-9999px',
              left: '-9999px',
            }),
      }}
    >
      {children}
    </Box>
  );

  return dialogCustomBox;
};
